import React, { useEffect, useMemo, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Paper, Typography, Chip, Box, Button,Tooltip, IconButton,
  Tabs, Tab, CircularProgress, Card, CardContent, Grid, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EmailIcon          from "@material-ui/icons/Email";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import VerifiedUserIcon   from "@material-ui/icons/VerifiedUser";
import GetAppIcon         from "@material-ui/icons/GetApp";
import CommentIcon        from "@material-ui/icons/Comment";
import SyncIcon           from "@material-ui/icons/Sync";
import PaymentIcon        from "@material-ui/icons/Payment";
import CheckCircleIcon    from "@material-ui/icons/CheckCircle";
import CancelIcon         from "@material-ui/icons/Cancel";
import ListAltIcon        from "@material-ui/icons/ListAlt";
import SearchIcon         from "@material-ui/icons/Search";
import * as XLSX from "xlsx";
import HttpService from "../../services/httpService";


const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
  },
  tableWrap: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    "& .MuiTableCell-head": {
      fontSize: "0.78rem !important",
      letterSpacing: 0.4,
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      padding: "12px 12px",
      fontWeight: "700 !important",
    },
    "& .MuiTableCell-body": {
      fontSize: "0.85rem",
      padding: "10px 12px",
      borderBottom: "1px solid #eef2f7",
    },
    "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
      backgroundColor: "#f8fafc",
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: "#eff6ff",
    },
  },
  tableContainer: {
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  },
  tabsPaper: {
    marginBottom: 20,
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 20px rgba(15,23,42,0.07)",
  },
  syncButton: {
    height: 40,
    fontSize: 13,
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    borderColor: "#2563eb",
    color: "#2563eb",
    background: "#fff",
  },
  downloadButton: {
    height: 40,
    fontSize: 13,
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    backgroundColor: "#0f172a",
    color: "#fff",
  },
  amountText: {
    fontWeight: 700,
    color: "#059669",
  },
  summaryCard: {
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 20px rgba(15,23,42,0.07)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
    },
  },
  statLabel: {
    color: "#64748b",
    fontSize: "0.95rem",
    fontWeight: 800,
  },
  utrText: {
    fontWeight: 700,
    color: "#1d4ed8",
  },
}));


const CATEGORY_TABS = [
  { key: "ec_payment",     label: "Misc Payment" },
  { key: "referral",       label: "Referral" },
  { key: "refund",         label: "Refund" },
  { key: "siteVisit",      label: "Site Visit" },
  { key: "channelPartner", label: "Channel Partner" },
];
const CATEGORY_KEYS = CATEGORY_TABS.map((t) => t.key);

const getStatusChip = (status) => {
  const colorMap = {
    Approved: "#22c55e",
    Rejected: "#ef4444",
    Pending: "#6b7280",
    PROCESSED: "#22c55e",
    SENT_TO_BANK: "#6b7280",
    RETURNED: "#ef4444",
    FAILED: "#ef4444",
    PENDING: "#6b7280",
  };
  return (
    <Chip
      label={status}
      size="small"
      style={{
        backgroundColor: colorMap[status] || "#e2e8f0",
        color: "#ffffff",
        fontWeight: 600,
      }}
    />
  );
};


const formatPaymentDate = (bankProcessedDate) => {
  const date = bankProcessedDate ? new Date(bankProcessedDate) : new Date();
  return date.toLocaleDateString("en-IN");
};

const HEADER_BG = "#1e293b";
const headerCellSx = (bg = HEADER_BG) => ({
  backgroundColor: bg,
  color: "#fff",
  fontWeight: 700,
  fontSize: "0.78rem",
  whiteSpace: "nowrap",
});


const SummaryCards = ({ classes, stats }) => (
  <Grid container spacing={2} style={{ marginBottom: 20 }}>
    {stats.map(({ label, value, color, bg, Icon }) => (
      <Grid item xs={12} sm={6} md={3} key={label}>
        <Card className={classes.summaryCard} style={{ borderLeft: `5px solid ${color}` }}>
          <CardContent style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography className={classes.statLabel}>{label}</Typography>
              <Typography style={{ fontSize: "1.4rem", fontWeight: 800, color }}>{value}</Typography>
            </Box>
            <Box style={{ background: bg, color, borderRadius: 12, padding: 10, display: "flex" }}>
              <Icon />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);


const SimpleUtrTable = ({ classes, columns, rows, stats, loading, page, rowsPerPage, totalDocs, onPageChange, onRowsPerPageChange, onRowClick, emptyMessage }) => {
  if (loading) {
    return (
      <Typography variant="h6" style={{ marginTop: 16, color: "#666" }}>
        Loading....
      </Typography>
    );
  }

  return (
    <>
      {stats && <SummaryCards classes={classes} stats={stats} />}
      <Paper className={classes.tableContainer}>
        <TableContainer style={{ maxHeight: 600 }}>
          <Table className={classes.tableWrap} stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.key} style={headerCellSx(col.headerBg)}>{col.header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" style={{ color: "#94a3b8", padding: 24 }}>
                    {emptyMessage || "No records found."}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row, index) => (
                <TableRow
                  key={index}
                  onClick={() => onRowClick && onRowClick(row)}
                  style={onRowClick ? { cursor: "pointer" } : undefined}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} className={col.className}>{col.render(row)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalDocs}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[100, 200, 300, 500]}
        />
      </Paper>
    </>
  );
};


const DETAIL_FIELDS = {
  SO: [
    { label: "SO Number", key: "soNumber" },
    { label: "Vendor", key: "vendorName" },
    { label: "UTR", key: "utrNumber" },
    { label: "Amount", key: "amount", isAmount: true },
    { label: "Raised Amount", key: "raisedPayment", isAmount: true },
    { label: "Account", key: "accountNumber" },
    { label: "IFSC", key: "ifsc" },
    { label: "Bank", key: "bankName" },
    { label: "Email", key: "email" },
    { label: "Raised By", key: "raisedBy" },
    { label: "Approved By", key: "approvedBy" },
    { label: "Payment Date", key: "date" },
    // { label: "Status", key: "hdfcStatus" },
    { label: "Remark", key: "remark" },
  ],
  SO_GST: [
    { label: "Order ID", key: "orderId" },
    { label: "Invoice Number", key: "invoiceNumber" },
    { label: "Vendor", key: "vendorName" },
    { label: "UTR", key: "utrNumber" },
    { label: "Base Amount", key: "baseAmount", isAmount: true },
    { label: "GST Rate (%)", key: "gstRate" },
    { label: "Amount", key: "amount", isAmount: true },
    { label: "Raised By", key: "raisedBy" },
    { label: "Raised At", key: "raisedAt", isDate: true },
    { label: "Approved By", key: "approvedBy" },
    { label: "Approved At", key: "approvedAt", isDate: true },
    { label: "Payment Date", key: "bankProcessedDate", isDate: true },
    // { label: "Status", key: "bankStatus" },
    { label: "Remark", key: "bankRemark" },
  ],
  ec_payment: [
    { label: "Payee", key: "payeeName" },
    { label: "Payment Type", key: "paymentType" },
    { label: "Payment Month", key: "paymentMonth" },
    { label: "UTR", key: "utrNumber" },
    { label: "Amount", key: "amount", isAmount: true },
    { label: "Account", key: "accountNumber" },
    { label: "IFSC", key: "ifsc" },
    { label: "Bank", key: "bankName" },
    { label: "Raised By", key: "raisedBy" },
    { label: "Approved By", key: "approvedBy" },
    { label: "Payment Date", key: "date", isDate: true },
    { label: "Status", key: "bankStatus" },
    { label: "Remark", key: "remark" },
  ],
  referral: [
    { label: "Lead Number", key: "reference" },
    { label: "Payment Type", key: "paymentType" },
    { label: "Referred By", key: "referredBy" },
    { label: "Referred To", key: "referredTo" },
    { label: "Payee", key: "payeeName" },
    { label: "UTR", key: "utrNumber" },
    { label: "Amount", key: "amount", isAmount: true },
    { label: "Account", key: "accountNumber" },
    { label: "IFSC", key: "ifsc" },
    { label: "Bank", key: "bankName" },
    { label: "Payment Date", key: "date", isDate: true },
    { label: "Status", key: "bankStatus" },
    { label: "Remark", key: "remark" },
  ],
  refund: [
    { label: "Lead Number", key: "reference" },
    { label: "Payee", key: "payeeName" },
    { label: "UTR", key: "utrNumber" },
    { label: "Amount", key: "amount", isAmount: true },
    { label: "Account", key: "accountNumber" },
    { label: "IFSC", key: "ifsc" },
    { label: "Bank", key: "bankName" },
    { label: "Refund Raised Date", key: "refundRaiseDate", isDate: true },
    { label: "Refund Approved (Finance) Date", key: "refundApprovedFinanceDate", isDate: true },
    { label: "Payment Date", key: "date", isDate: true },
    { label: "Status", key: "bankStatus" },
    { label: "Remark", key: "remark" },
  ],
  siteVisit: [
    { label: "Lead Number", key: "reference" },
    { label: "Referrer Lead Number", key: "referrerLeadNumber" },
    { label: "Payee", key: "payeeName" },
    { label: "UTR", key: "utrNumber" },
    { label: "Amount", key: "amount", isAmount: true },
    { label: "Account", key: "accountNumber" },
    { label: "IFSC", key: "ifsc" },
    { label: "Bank", key: "bankName" },
    { label: "Raised By", key: "raisedBy" },
    { label: "DGM/AGM Approved By", key: "dgmAgmApprovedBy" },
    { label: "Finance Manager Approved By", key: "financeManagerApprovedBy" },
    { label: "Payment Date", key: "date", isDate: true },
    { label: "Status", key: "bankStatus" },
    { label: "Remark", key: "remark" },
  ],
  channelPartner: [
    { label: "Payout Code", key: "payoutCode" },
    { label: "Channel Partner", key: "channelPartnerName" },
    { label: "Lead Number", key: "reference" },
    { label: "UTR", key: "utrNumber" },
    { label: "Amount", key: "amount", isAmount: true },
    { label: "Account", key: "accountNumber" },
    { label: "IFSC", key: "ifsc" },
    { label: "Bank", key: "bankName" },
    { label: "Raised By", key: "raisedBy" },
    { label: "Verified By", key: "verifiedBy" },
    { label: "Payment Date", key: "date", isDate: true },
    { label: "Status", key: "bankStatus" },
    { label: "Remark", key: "remark" },
  ],
};

const PaymentDetailsDialog = ({ open, onClose, type, row }) => {
  const fields = DETAIL_FIELDS[type] || [];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ background: "#1e293b", color: "#fff", fontWeight: 800 }}>
        Payment Details
      </DialogTitle>
      <DialogContent style={{ paddingTop: 20 }}>
        {row && (
          <Grid container spacing={2}>
            {fields.map(({ label, key, isAmount, isDate }) => {
              const raw = row[key];
              if (raw === undefined || raw === null || raw === "") return null;
              const value = isAmount
                ? `₹${Number(raw).toLocaleString("en-IN")}`
                : isDate
                  ? new Date(raw).toLocaleDateString("en-IN")
                  : raw;
              return (
                <Grid item xs={12} sm={6} key={key}>
                  <Typography style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                    {label}
                  </Typography>
                  <Typography style={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f172a" }}>
                    {value}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} style={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};


const CategoryUtrTable = ({ classes, rows, loading, totalDocs, page, rowsPerPage, onPageChange, onRowsPerPageChange, onRowClick }) => {
  const stats = useMemo(() => {
    const processed = rows.filter((r) => r.bankStatus === "PROCESSED").length;
    const returned = rows.filter((r) => r.bankStatus === "RETURNED" || r.bankStatus === "FAILED").length;
    const totalAmount = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    return [
      { label: "Total Records", value: totalDocs, color: "#2563eb", bg: "#eff6ff", Icon: ListAltIcon },
      { label: "Processed", value: processed, color: "#059669", bg: "#ecfdf5", Icon: CheckCircleIcon },
      { label: "Returned / Failed", value: returned, color: "#dc2626", bg: "#fef2f2", Icon: CancelIcon },
      { label: "Total Amount (₹)", value: totalAmount.toLocaleString("en-IN"), color: "#7c3aed", bg: "#f5f3ff", Icon: PaymentIcon },
    ];
  }, [rows, totalDocs]);

  if (loading) {
    return (
      <Typography variant="h6" style={{ marginTop: 16, color: "#666" }}>
        Loading....
      </Typography>
    );
  }

  return (
    <>
      <SummaryCards classes={classes} stats={stats} />
      <Paper className={classes.tableContainer}>
        <TableContainer style={{ maxHeight: 600 }}>
          <Table className={classes.tableWrap} stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell style={headerCellSx()}>S.No</TableCell>
                <TableCell style={headerCellSx()}>Reference</TableCell>
                <TableCell style={headerCellSx()}>Payee</TableCell>
                <TableCell style={headerCellSx()}>UTR</TableCell>
                <TableCell style={headerCellSx()}>Account</TableCell>
                <TableCell style={headerCellSx("#2563eb")}>Amount (₹)</TableCell>
                <TableCell style={headerCellSx()}>IFSC</TableCell>
                <TableCell style={headerCellSx()}>Bank</TableCell>
                <TableCell style={headerCellSx()}>Payment Date</TableCell>
                <TableCell style={headerCellSx()}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" style={{ color: "#94a3b8", padding: 24 }}>
                    No UTRs synced for this category yet.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row, index) => (
                <TableRow key={index} onClick={() => onRowClick && onRowClick(row)} style={{ cursor: "pointer" }}>
                  <TableCell>{row.serialNo}</TableCell>
                  <TableCell>{row.reference}</TableCell>
                  <TableCell>{row.payeeName}</TableCell>
                  <TableCell className={classes.utrText}>{row.utrNumber}</TableCell>
                  <TableCell>{row.accountNumber}</TableCell>
                  <TableCell className={classes.amountText}>
                    {Number(row.amount || 0).toLocaleString("en-IN")}
                  </TableCell>
                  <TableCell>{row.ifsc}</TableCell>
                  <TableCell>{row.bankName}</TableCell>
                  <TableCell>{formatPaymentDate(row.date)}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {getStatusChip(row.bankStatus)}
                      {row.remark && (
                        <Tooltip title={row.remark} arrow>
                          <IconButton size="small" style={{ marginLeft: 4 }}>
                            <CommentIcon fontSize="small" style={{ color: "#64748b" }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalDocs}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[100, 200, 300, 500]}
        />
      </Paper>
    </>
  );
};

const ReverseFileViewer = () => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState("SO");

  // ── SO tab state (unchanged — default view) ──────────────────────────────
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [reverseFileData, setReverseFileData] = useState([]);
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(200);
  const [totalDocs, setTotalDocs]     = useState(0);

  // ── Category tabs state (Misc/Referral/Refund/Site Visit/Channel Partner) ─
  const [categoryRows, setCategoryRows]       = useState([]);
  const [categoryTotal, setCategoryTotal]     = useState(0);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryPage, setCategoryPage]       = useState(0);
  const [categoryRowsPerPage, setCategoryRowsPerPage] = useState(200);
  const [syncing, setSyncing] = useState(false);

  // ── SO With GST tab state ────────────────────────────────────────────────
  const [gstRows, setGstRows]           = useState([]);
  const [gstTotal, setGstTotal]         = useState(0);
  const [gstLoading, setGstLoading]     = useState(false);
  const [gstPage, setGstPage]           = useState(0);
  const [gstRowsPerPage, setGstRowsPerPage] = useState(200);

  // ── Payment details dialog — opened by clicking any row, any tab ────────
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsRow, setDetailsRow]   = useState(null);
  const handleRowClick = (row) => {
    setDetailsRow(row);
    setDetailsOpen(true);
  };

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const soStats = useMemo(() => {
    const totalAmount = reverseFileData.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const totalRaised = reverseFileData.reduce((sum, r) => sum + (Number(r.raisedPayment) || 0), 0);
    const utrSynced = reverseFileData.filter((r) => r.utrNumber && r.utrNumber !== "----").length;
    return [
      { label: "Total Records", value: totalDocs, color: "#2563eb", bg: "#eff6ff", Icon: ListAltIcon },
      { label: "UTR Synced", value: utrSynced, color: "#059669", bg: "#ecfdf5", Icon: CheckCircleIcon },
      { label: "Total Amount (₹)", value: totalAmount.toLocaleString("en-IN"), color: "#7c3aed", bg: "#f5f3ff", Icon: PaymentIcon },
      { label: "Raised Amount (₹)", value: totalRaised.toLocaleString("en-IN"), color: "#b45309", bg: "#fffbeb", Icon: PaymentIcon },
    ];
  }, [reverseFileData, totalDocs]);

  const fetchData = async (currentPage, limit, search) => {
    try {
      setLoading(true);
      setError(null);

      const response = await HttpService.responseDataAfterUploadingToHDFC({
        page : currentPage + 1,   // convert MUI 0-index → backend 1-index
        limit,
        search,
      });

      if (response.data?.status === "success") {
        const { data: rows, totalDocs: total } = response.data;

        const mappedData = rows.map((item) => ({
          transactionType : item.paymentType      || "N",
          soNumber        : item.orderId           || "N/A",
          utrNumber       : item.utrNumber         || "----",
          accountNumber   : item.accountNumber     || "",
          amount          : item.amount            || 0,
          vendorName      : item.vendorName        || "",
          date            : item.date              || "",
          ifsc            : item.ifsc              || "",
          bankName        : item.bankName          || "",
          branch          : item.branch            || "",
          email           : item.email             || "",
          hdfcStatus      : item.bankStatus        || "-",
          serialNo        : item.serialNo,          // global serial from backend
          raisedPayment   : item.aggregatedAmount   || 0,
          remark          : item.remarks            || "",
          raisedBy        : item.raisedBy           || "",
          approvedBy      : item.approvedBy         || "",
        }));

        setReverseFileData(mappedData);
        setTotalDocs(total);
      } else {
        setError("No data received from API");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to fetch data from API");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryData = async (type, currentPage, limit, search) => {
    try {
      setCategoryLoading(true);
      const response = await HttpService.getReverseUtrsByCategory(type, {
        page: currentPage + 1,
        limit,
        search,
      });
      if (response.data?.success) {
        setCategoryRows(response.data.data?.data || []);
        setCategoryTotal(response.data.data?.totalDocs || 0);
      } else {
        setCategoryRows([]);
        setCategoryTotal(0);
      }
    } catch (err) {
      console.error("Error fetching category UTRs:", err);
      setCategoryRows([]);
      setCategoryTotal(0);
    } finally {
      setCategoryLoading(false);
    }
  };

  const fetchGstData = async (currentPage, limit, search) => {
    try {
      setGstLoading(true);
      const response = await HttpService.gstHdfcReverseResponseAfterUploading({
        page: currentPage + 1,
        limit,
        search,
      });
      if (response.data?.status === "success") {
        setGstRows(response.data.data || []);
        setGstTotal(response.data.totalDocs || 0);
      } else {
        setGstRows([]);
        setGstTotal(0);
      }
    } catch (err) {
      console.error("Error fetching GST UTRs:", err);
      setGstRows([]);
      setGstTotal(0);
    } finally {
      setGstLoading(false);
    }
  };

  const gstStats = useMemo(() => {
    const processed = gstRows.filter((r) => r.bankStatus === "PROCESSED").length;
    const totalAmount = gstRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    return [
      { label: "Total Records", value: gstTotal, color: "#2563eb", bg: "#eff6ff", Icon: ListAltIcon },
      { label: "Processed", value: processed, color: "#059669", bg: "#ecfdf5", Icon: CheckCircleIcon },
      { label: "Total Amount (₹)", value: totalAmount.toLocaleString("en-IN"), color: "#7c3aed", bg: "#f5f3ff", Icon: PaymentIcon },
    ];
  }, [gstRows, gstTotal]);

  // Debounce the raw input before it drives any fetch.
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // A new search always starts back at page 1, whichever tab it's on.
  useEffect(() => {
    setPage(0);
    setCategoryPage(0);
    setGstPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (activeTab === "SO") {
      fetchData(page, rowsPerPage, searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, rowsPerPage, searchTerm]);

  useEffect(() => {
    if (CATEGORY_KEYS.includes(activeTab)) {
      fetchCategoryData(activeTab, categoryPage, categoryRowsPerPage, searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, categoryPage, categoryRowsPerPage, searchTerm]);

  useEffect(() => {
    if (activeTab === "SO_GST") {
      fetchGstData(gstPage, gstRowsPerPage, searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, gstPage, gstRowsPerPage, searchTerm]);

  const handleTabChange = (_event, newTab) => {
    setActiveTab(newTab);
    setCategoryPage(0);
    setGstPage(0);
  };

  const syncButtonLabel = activeTab === "SO"
    ? "Sync Without GST UTR"
    : activeTab === "SO_GST"
      ? "Sync With GST UTR"
      : "Sync UTRs";

  const searchPlaceholder = activeTab === "SO" || activeTab === "SO_GST"
    ? "Search by Order ID"
    : activeTab === "ec_payment"
      ? "Search by Payee Name"
      : "Search by Unique Lead Number";

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);   // reset to first page on limit change
  };

  const handleCategoryChangePage = (_event, newPage) => {
    setCategoryPage(newPage);
  };

  const handleCategoryChangeRowsPerPage = (event) => {
    setCategoryRowsPerPage(parseInt(event.target.value, 10));
    setCategoryPage(0);
  };

  const handleGstChangePage = (_event, newPage) => setGstPage(newPage);
  const handleGstChangeRowsPerPage = (event) => {
    setGstRowsPerPage(parseInt(event.target.value, 10));
    setGstPage(0);
  };

  const canSyncActiveTab = activeTab === "SO" || activeTab === "SO_GST" || CATEGORY_KEYS.includes(activeTab);

  const handleSyncFromS3 = async () => {
    try {
      setSyncing(true);
      if (activeTab === "SO") {
        await HttpService.syncSoReverseUtrsFromS3();
        await fetchData(page, rowsPerPage, searchTerm);
      } else if (activeTab === "SO_GST") {
        await HttpService.syncGstReverseUtrsFromS3();
        await fetchGstData(gstPage, gstRowsPerPage, searchTerm);
      } else {
        await HttpService.syncReverseUtrsFromS3();
        await fetchCategoryData(activeTab, categoryPage, categoryRowsPerPage, searchTerm);
      }
    } catch (err) {
      console.error("Error syncing reverse UTR files from S3:", err);
    } finally {
      setSyncing(false);
    }
  };

  const handleDownloadExcel = () => {
    const dataToExport = activeTab === "SO" ? reverseFileData
      : activeTab === "SO_GST" ? gstRows
      : categoryRows;
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ReverseFile");
    XLSX.writeFile(workbook, "reverse_file_data.xlsx");
  };

  return (
    <div className={classes.root}>
      {/* ── Page header + actions ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 12, marginBottom: 20 }}>
        <Box>
          <Typography variant="h5" style={{ fontWeight: 800, color: "#0f172a" }}>
            Snorkel Response
          </Typography>
          <Typography variant="body2" style={{ color: "#64748b" }}>
            HDFC bank UTRs and statuses for SO and every other payment category, synced from the reverse report.
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          {canSyncActiveTab && (
            <Button
              variant="outlined"
              className={classes.syncButton}
              startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={handleSyncFromS3}
              disabled={syncing}
            >
              {syncing ? "Syncing..." : syncButtonLabel}
            </Button>
          )}
          <Button
            variant="contained"
            className={classes.downloadButton}
            startIcon={<GetAppIcon />}
            onClick={handleDownloadExcel}
          >
            Download Excel
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} className={classes.tabsPaper}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="SO Without GST" value="SO" style={{ textTransform: "none", fontWeight: 800, fontSize: "0.95rem" }} />
          <Tab label="SO With GST" value="SO_GST" style={{ textTransform: "none", fontWeight: 800, fontSize: "0.95rem" }} />
          {CATEGORY_TABS.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} style={{ textTransform: "none", fontWeight: 800, fontSize: "0.95rem" }} />
          ))}
        </Tabs>
      </Paper>

      <Box style={{ marginBottom: 20 }}>
        <TextField
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={searchPlaceholder}
          variant="outlined"
          size="small"
          style={{ width: 320, maxWidth: "100%", background: "#fff", borderRadius: 10 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" style={{ color: "#64748b" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {activeTab === "SO" ? (
        error ? (
          <Typography variant="h6" style={{ marginTop: 16, color: "#ef4444" }}>
            {error}
          </Typography>
        ) : loading ? (
          <Typography variant="h6" style={{ marginTop: 16, color: "#666" }}>
            Loading....
          </Typography>
        ) : (
          <>
          <SummaryCards classes={classes} stats={soStats} />
          <Paper className={classes.tableContainer}>
            <TableContainer style={{ maxHeight: 600 }}>
              <Table className={classes.tableWrap} stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={headerCellSx()}>S.No</TableCell>
                    <TableCell style={headerCellSx()}>Type</TableCell>
                    <TableCell style={headerCellSx()}>SO Number</TableCell>
                    <TableCell style={headerCellSx()}>UTR</TableCell>
                    <TableCell style={headerCellSx()}>Account</TableCell>
                    <TableCell style={headerCellSx("#2563eb")}>Amount (₹)</TableCell>
                    <TableCell style={headerCellSx("#059669")}>Raised Amount (₹)</TableCell>
                    <TableCell style={headerCellSx()}>Vendor</TableCell>
                    <TableCell style={headerCellSx()}>Payment Date</TableCell>
                    <TableCell style={headerCellSx()}>IFSC</TableCell>
                    <TableCell style={headerCellSx()}>Bank</TableCell>
                    <TableCell style={headerCellSx()}>Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reverseFileData.map((row, index) => (
                    <TableRow key={index} onClick={() => handleRowClick(row)} style={{ cursor: "pointer" }}>
                      <TableCell>{row.serialNo}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.transactionType}
                          color="primary"
                          size="small"
                          icon={<VerifiedUserIcon fontSize="small" />}
                        />
                      </TableCell>
                      <TableCell>{row.soNumber}</TableCell>
                      <TableCell className={classes.utrText}>{row.utrNumber}</TableCell>
                      <TableCell>{row.accountNumber}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" className={classes.amountText}>
                          {Number(row.amount).toLocaleString("en-IN")}
                          {row.remark && row.remark !== "-" && (
                            <Tooltip title={row.remark} arrow>
                              <IconButton size="small" style={{ marginLeft: 4 }}>
                                <CommentIcon fontSize="small" style={{ color: "#64748b" }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell className={classes.amountText}>
                        {Number(row.raisedPayment).toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell>{row.vendorName}</TableCell>
                      <TableCell>{row.date}</TableCell>
                      <TableCell>{row.ifsc}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <AccountBalanceIcon fontSize="small" style={{ marginRight: 4, color: "#64748b" }} />
                          {row.bankName}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <EmailIcon fontSize="small" style={{ marginRight: 4, color: "#64748b" }} />
                          {row.email}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ── Pagination bar ── */}
            <TablePagination
              component="div"
              count={totalDocs}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[100, 200, 300, 500, 1000]}
            />
          </Paper>
          </>
        )
      ) : activeTab === "SO_GST" ? (
        <SimpleUtrTable
          classes={classes}
          stats={gstStats}
          loading={gstLoading}
          rows={gstRows}
          totalDocs={gstTotal}
          page={gstPage}
          rowsPerPage={gstRowsPerPage}
          onPageChange={handleGstChangePage}
          onRowsPerPageChange={handleGstChangeRowsPerPage}
          onRowClick={handleRowClick}
          emptyMessage="No GST UTRs synced yet."
          columns={[
            { key: "serialNo", header: "S.No", render: (r) => r.serialNo },
            { key: "orderId", header: "Order ID", render: (r) => r.orderId },
            { key: "vendorName", header: "Vendor", render: (r) => r.vendorName },
            { key: "invoiceNumber", header: "Invoice Number", render: (r) => r.invoiceNumber },
            { key: "utrNumber", header: "UTR", className: classes.utrText, render: (r) => r.utrNumber },
            { key: "amount", header: "Amount (₹)", headerBg: "#2563eb", className: classes.amountText, render: (r) => Number(r.amount || 0).toLocaleString("en-IN") },
            { key: "raisedBy", header: "Raised By", render: (r) => r.raisedBy },
            { key: "approvedBy", header: "Approved By", render: (r) => r.approvedBy },
            { key: "bankProcessedDate", header: "Payment Date", render: (r) => formatPaymentDate(r.bankProcessedDate) },
            {
              key: "bankStatus",
              header: "Status",
              render: (r) => (
                <Box display="flex" alignItems="center">
                  {getStatusChip(r.bankStatus)}
                  {r.bankRemark && (
                    <Tooltip title={r.bankRemark} arrow>
                      <IconButton size="small" style={{ marginLeft: 4 }}>
                        <CommentIcon fontSize="small" style={{ color: "#64748b" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ),
            },
          ]}
        />
      ) : (
        <CategoryUtrTable
          classes={classes}
          rows={categoryRows}
          loading={categoryLoading}
          totalDocs={categoryTotal}
          page={categoryPage}
          rowsPerPage={categoryRowsPerPage}
          onPageChange={handleCategoryChangePage}
          onRowsPerPageChange={handleCategoryChangeRowsPerPage}
          onRowClick={handleRowClick}
        />
      )}

      <PaymentDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        type={activeTab}
        row={detailsRow}
      />
    </div>
  );
};

export default ReverseFileViewer;
